import React, { useState, useRef, useEffect } from 'react';
import styles from './Header.module.css';

export default function Header({ recipeName, currentRecipeId, calc, onOpenDrawer, onScale, onExport, onShare, onHelp, onOpenDB, onOpenTools, onImport, onClear, onBackup, onOpenPricing }) {
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [modal, setModal] = useState(null); // null | 'saveNew' | 'saveAs' | 'rename'
  const [nameInput, setNameInput] = useState('');
  const [saved, setSaved] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setSaveMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openModal = (type) => {
    setSaveMenuOpen(false);
    setNameInput(recipeName || '');
    setSaved(false);
    setModal(type);
  };

  const handleSave = () => {
    // If recipe already saved → overwrite silently (no modal)
    if (currentRecipeId) {
      calc.saveRecipe(recipeName, 'overwrite');
      flashSaved();
    } else {
      // No id yet → same as Save New
      openModal('saveNew');
    }
  };

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const submitModal = () => {
    const name = nameInput.trim();
    if (!name) return;
    if (modal === 'saveNew' || modal === 'saveAs') {
      calc.saveRecipe(name, modal === 'saveAs' ? 'saveAs' : 'new');
    } else if (modal === 'rename') {
      if (currentRecipeId) calc.renameRecipe(currentRecipeId, name);
      else calc.saveRecipe(name, 'new');
    }
    flashSaved();
    setModal(null);
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.inner}>

          <a
            className={styles.backToSite}
            href="https://mesarobles.com/craft"
            title="Back to Mesa Robles"
          >
            ← Mesa Robles
          </a>

          {/* Brand */}
          <div className={styles.brand}>
            <span className={styles.logo}>🌭</span>
            <div className={styles.brandText}>
              <div className={styles.appName}>LinksLab</div>
              <div className={styles.currentRecipe}>
                {recipeName ? (
                  <>
                    <span className={styles.currentName}>{recipeName}</span>
                    {currentRecipeId && (
                      <span className={styles.savedBadge}>saved</span>
                    )}
                    {!currentRecipeId && (
                      <span className={styles.unsavedBadge}>unsaved</span>
                    )}
                  </>
                ) : (
                  <span className={styles.noName}>New Recipe</span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className={styles.actions}>

            {/* ── Group 1: Recipe lifecycle ── */}
            <button className={styles.btnGhost} onClick={onClear}>
              New Recipe
            </button>
            <button className={styles.btnGhost} onClick={onOpenDrawer}>
              My Recipes
            </button>
            <button className={styles.btnGhost} onClick={onImport} title="Import recipe from text">
              📥 Import
            </button>

            {/* Save split button */}
            <div className={styles.saveGroup} ref={menuRef}>
              <button
                className={`${styles.btnAccent} ${saved ? styles.btnSaved : ''}`}
                onClick={handleSave}
                title={currentRecipeId ? 'Save (overwrite current)' : 'Save recipe'}
              >
                {saved ? '✓ Saved' : 'Save'}
              </button>
              <button
                className={styles.btnAccentCaret}
                onClick={() => setSaveMenuOpen(o => !o)}
                title="More save options"
              >
                ▾
              </button>

              {saveMenuOpen && (
                <div className={styles.saveMenu}>
                  <button className={styles.saveMenuItem} onClick={handleSave}>
                    <span className={styles.saveMenuIcon}>💾</span>
                    <div>
                      <div className={styles.saveMenuLabel}>Save</div>
                      <div className={styles.saveMenuDesc}>
                        {currentRecipeId ? 'Overwrite current recipe' : 'Save as new recipe'}
                      </div>
                    </div>
                  </button>
                  <button className={styles.saveMenuItem} onClick={() => openModal('saveAs')}>
                    <span className={styles.saveMenuIcon}>📋</span>
                    <div>
                      <div className={styles.saveMenuLabel}>Save As…</div>
                      <div className={styles.saveMenuDesc}>Save a copy with a new name</div>
                    </div>
                  </button>
                  <button className={styles.saveMenuItem} onClick={() => openModal('rename')}>
                    <span className={styles.saveMenuIcon}>✎</span>
                    <div>
                      <div className={styles.saveMenuLabel}>Rename…</div>
                      <div className={styles.saveMenuDesc}>Change the recipe name</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* ── Divider ── */}
            <div className={styles.actionDivider} />

            {/* ── Group 2: Tools & utilities ── */}
            <button className={styles.btnGhost} onClick={onScale}>
              Scale Recipe
            </button>
            <button className={styles.btnGhost} onClick={onOpenDB} title="Ingredient Database">
              🗂️ Ingredients
            </button>
            <button className={styles.btnGhost} onClick={onOpenTools} title="Tools & Calculators">
              🔧 Tools
            </button>
            <button className={styles.btnGhost} onClick={onOpenPricing} title="Price this batch for sale">
              💵 Pricing
            </button>
            <button className={styles.btnGhost} onClick={onExport} title="Export recipe to PDF">
              Export PDF
            </button>
            <button className={styles.btnGhost} onClick={onShare}
                    title="Publish this recipe on Mesa Robles">
              🌶️ Share
            </button>
            <button className={styles.btnGhost} onClick={onBackup} title="Back up or restore all your formulas">
              💾 Backup
            </button>
            <button className={styles.btnHelp} onClick={onHelp} title="How-To Guide">
              ?
            </button>

          </div>
        </div>
      </header>

      {/* ── Save / SaveAs / Rename modal ── */}
      {modal && (
        <div className={styles.modalOverlay} onClick={() => setModal(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            {saved ? (
              <div className={styles.savedConfirm}>
                <span className={styles.savedCheck}>✓</span>
                <span>{modal === 'rename' ? 'Renamed!' : 'Recipe saved!'}</span>
              </div>
            ) : (
              <>
                <div className={styles.modalTitle}>
                  {modal === 'saveNew' && 'Save Recipe'}
                  {modal === 'saveAs'  && 'Save As…'}
                  {modal === 'rename'  && 'Rename Recipe'}
                </div>
                <div className={styles.modalDesc}>
                  {modal === 'saveNew' && 'Give this recipe a name.'}
                  {modal === 'saveAs'  && 'Creates a new copy. Original is not changed.'}
                  {modal === 'rename'  && 'Change the name of this recipe.'}
                </div>
                <input
                  className={styles.modalInput}
                  type="text"
                  placeholder="Recipe name…"
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submitModal()}
                  autoFocus
                />
                <div className={styles.modalActions}>
                  <button className={styles.btnModalCancel} onClick={() => setModal(null)}>
                    Cancel
                  </button>
                  <button
                    className={styles.btnModalSave}
                    onClick={submitModal}
                    disabled={!nameInput.trim()}
                  >
                    {modal === 'rename' ? 'Rename' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
